/**
 * Перенос готовой воронки с карточек ВБ/ОЗОН/сайта в визуалы кабинета Я.Маркета
 * (T9). Форма страницы предпросмотра — по образцу `features/site/types.ts`
 * (`MpVisualImportRow`/`MpVisualSource`), но источники шире («Сайт» третьим
 * пунктом) и слева не карточка сайта, а оффер Маркета с его текущим фото.
 */

export type YmVisualSourceMarketplace = 'wb' | 'ozon' | 'site'

/** Карточка-источник визуалов: `key` — устойчивый идентификатор выбора (`wb:18:114655954`). */
export interface YmVisualSource {
  key: string
  marketplace: YmVisualSourceMarketplace
  sellerId: number
  cabinet: string
  sellerName: string
  externalId: string
  title: string
  photoCount: number
  /** Первые кадры для превью; переносится галерея целиком. */
  photos: string[]
  updatedAt: string | null
}

/** Строка предпросмотра: слева оффер Маркета, справа карточки-источники. */
export interface YmVisualImportRow {
  article: string
  title: string
  ymOffer: {
    cardStatus: string | null
    /** Ссылка карточки как есть — может быть мёртвой, площадка своих копий не отдаёт. */
    mainPicture: string | null
    /** Статус загрузки этого фото на Маркете (offer.mediaFiles.pictures[0].uploadState). */
    mainPictureState: string | null
  }
  /** Сколько визуалов кабинета Маркета уже лежит в ПИМ — «заменим», а не «добавим». */
  ksaYmVisualCount: number
  sources: YmVisualSource[]
  defaultSourceKey: string | null
}

export interface YmVisualImportPage {
  rows: YmVisualImportRow[]
  nextAfter: string | null
  total: number
}

/** Подмножество фильтров каталога, которым можно сузить выборку «по фильтру экрана». */
export interface YmVisualImportFilters {
  search?: string
  cardStatus?: string[]
  categoryId?: string
  errors?: '' | 'with' | 'without'
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}

const text = (value: unknown): string => (value === null || value === undefined ? '' : String(value))

const nullableText = (value: unknown): string | null => {
  const result = text(value).trim()
  return result ? result : null
}

const numberOr = (value: unknown, fallback: number): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function normalizeYmVisualSource(value: unknown): YmVisualSource {
  const source = asRecord(value)
  const marketplace = source.marketplace === 'ozon' || source.marketplace === 'site' ? source.marketplace : 'wb'
  return {
    key: text(source.key),
    marketplace,
    sellerId: numberOr(source.sellerId, 0),
    cabinet: text(source.cabinet),
    sellerName: text(source.sellerName),
    externalId: text(source.externalId),
    title: text(source.title),
    photoCount: numberOr(source.photoCount, 0),
    photos: Array.isArray(source.photos) ? source.photos.map((photo) => text(photo)) : [],
    updatedAt: nullableText(source.updatedAt),
  }
}

export function normalizeYmVisualImportRow(value: unknown): YmVisualImportRow {
  const source = asRecord(value)
  const offer = asRecord(source.ymOffer)
  return {
    article: text(source.article),
    title: text(source.title),
    ymOffer: {
      cardStatus: nullableText(offer.cardStatus),
      mainPicture: nullableText(offer.mainPicture),
      mainPictureState: nullableText(offer.mainPictureState),
    },
    ksaYmVisualCount: numberOr(source.ksaYmVisualCount, 0),
    sources: Array.isArray(source.sources) ? source.sources.map(normalizeYmVisualSource) : [],
    defaultSourceKey: nullableText(source.defaultSourceKey),
  }
}

export function normalizeYmVisualImportPage(value: unknown): YmVisualImportPage {
  const source = asRecord(value)
  const rows = Array.isArray(source.rows) ? source.rows : []
  return {
    rows: rows.map(normalizeYmVisualImportRow),
    nextAfter: nullableText(source.nextAfter),
    total: numberOr(source.total, rows.length),
  }
}
